import React from 'react';

const R_COLOR = '#dc3545';
const D_COLOR = '#0d6efd';
const CARD_ACCENT = '#94a3b8';
const PARTY_COLORS = { R: R_COLOR, D: D_COLOR, I: '#6f42c1', N: '#64748b' };
const PARTY_LABELS = { R: 'Republican', D: 'Democratic', I: 'Independent', N: 'Nonpartisan' };

const fmtVotes = (n) => n != null ? n.toLocaleString() : null;
const fmtPercent = (n) => typeof n === 'number' ? `${n.toFixed(1)}%` : null;

const PlaceholderAvatar = ({ party, size = 58 }) => {
  const color = PARTY_COLORS[party] || '#6c757d';
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"
      style={{ borderRadius: '50%', border: `3px solid ${color}` }} aria-hidden="true">
      <rect width="48" height="48" fill="#f1f3f5" rx="24" />
      <circle cx="24" cy="18" r="9" fill={color} opacity="0.45" />
      <path d="M6 44c0-9.941 8.059-18 18-18s18 8.059 18 18" fill={color} opacity="0.25" />
    </svg>
  );
};

const websiteIcon = (url) => {
  try {
    const domain = new URL(url).hostname.replace(/^www\./, '');
    if (domain === 'facebook.com') return { label: 'Facebook', color: '#1877f2' };
    if (domain === 'instagram.com') return { label: 'Instagram', color: '#e1306c' };
    if (domain === 'twitter.com' || domain === 'x.com') return { label: 'X', color: '#000' };
    if (domain === 'linkedin.com') return { label: 'LinkedIn', color: '#0a66c2' };
    return { label: 'Website', color: '#6c757d' };
  } catch {
    return { label: 'Website', color: '#6c757d' };
  }
};

const getResultLabel = (candidate, generalElection) => {
  const resultCandidate = generalElection?.candidates?.find((item) => item.id === candidate.id) || candidate;
  if (resultCandidate.general_votes == null) return 'Results pending';

  const percent = fmtPercent(resultCandidate.general_percent);
  return `${fmtVotes(resultCandidate.general_votes)} votes${percent ? ` | ${percent}` : ''}`;
};

const getCandidateLabel = (party) => {
  if (party === 'R' || party === 'D') return `${PARTY_LABELS[party]} nominee`;
  return `${PARTY_LABELS[party] || 'Other'} candidate`;
};

const CandidateLinks = ({ candidate }) => {
  const site = candidate.website_url ? websiteIcon(candidate.website_url) : null;

  if (!site && !candidate.finance_url) return null;

  return (
    <div className="d-flex justify-content-center gap-2 mt-2 flex-wrap">
      {site && (
        <a href={candidate.website_url} target="_blank" rel="noopener noreferrer"
          title={site.label} style={{ fontSize: '0.72rem', color: site.color, textDecoration: 'none', fontWeight: 700 }}>
          {site.label}
        </a>
      )}
      {candidate.finance_url && (
        <a href={candidate.finance_url} target="_blank" rel="noopener noreferrer"
          title="Campaign finance" style={{ fontSize: '0.72rem', color: '#198754', textDecoration: 'none', fontWeight: 700 }}>
          Finance
        </a>
      )}
    </div>
  );
};

const CandidateAvatar = ({ candidate, party, color }) => {
  const [imageFailed, setImageFailed] = React.useState(false);

  if (!candidate.image_url || imageFailed) {
    return <PlaceholderAvatar party={party} size={64} />;
  }

  return (
    <img
      src={candidate.image_url}
      alt={candidate.name}
      width={64}
      height={64}
      style={{
        borderRadius: '50%',
        objectFit: 'cover',
        objectPosition: candidate.image_position || '50% 35%',
        border: `3px solid ${color}`,
      }}
      onError={() => setImageFailed(true)}
    />
  );
};

const CandidateMatchupCard = ({ candidate, generalElection }) => {
  const party = candidate.party;
  const color = PARTY_COLORS[party] || '#6c757d';
  const primaryVotes = fmtVotes(candidate.primary_votes);

  return (
    <div className="rounded p-3 text-center h-100" style={{
      border: `1px solid ${color}40`,
      background: `${color}0c`,
      minWidth: 0,
    }}>
      <div className="d-flex justify-content-center mb-2">
        <CandidateAvatar candidate={candidate} party={party} color={color} />
      </div>

      <div className="fw-bold" style={{ fontSize: '0.9rem', lineHeight: 1.2 }}>{candidate.name}</div>
      <div className="d-flex justify-content-center align-items-center gap-2 mt-2 flex-wrap">
        <span className="badge" style={{ backgroundColor: color, fontSize: '0.7rem' }}>{party}</span>
        <span className="badge text-bg-light" style={{ fontSize: '0.68rem', border: `1px solid ${color}33` }}>
          {getCandidateLabel(party)}
        </span>
      </div>

      {primaryVotes && (
        <div className="mt-2" style={{ fontSize: '0.76rem', color: '#198754', fontWeight: 700 }}>
          Won May 5 county primary with {primaryVotes} votes
        </div>
      )}

      <div className="mt-2" style={{ fontSize: '0.78rem', color: '#6c757d', fontWeight: 700 }}>
        {getResultLabel(candidate, generalElection)}
      </div>

      <CandidateLinks candidate={candidate} />
    </div>
  );
};

const EmptyNomineeCard = ({ party }) => {
  const color = PARTY_COLORS[party] || '#6c757d';
  const label = PARTY_LABELS[party] || 'Other party';

  return (
    <div className="rounded p-3 text-center h-100 d-flex align-items-center justify-content-center"
      style={{ border: `1px dashed ${color}55`, background: '#fff', color: '#6c757d', minHeight: 188 }}>
      <div>
        <div className="fw-bold" style={{ fontSize: '0.85rem' }}>No {label} nominee</div>
        <div style={{ fontSize: '0.72rem' }}>This race is unopposed in the current data.</div>
      </div>
    </div>
  );
};

const Matchup = ({ rCandidate, dCandidate, generalElection, topParty }) => {
  const ordered = topParty === 'D'
    ? [{ party: 'D', candidate: dCandidate }, { party: 'R', candidate: rCandidate }]
    : [{ party: 'R', candidate: rCandidate }, { party: 'D', candidate: dCandidate }];
  const statusLabel = generalElection?.precinctsReporting != null
    ? `${generalElection.precinctsReporting} of ${generalElection.precinctsTotal} precincts`
    : 'Results pending';

  return (
    <div className="rounded p-3" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
      <div className="text-center fw-bold mb-3" style={{ fontSize: '0.76rem', letterSpacing: '0.08em', color: '#495057' }}>
        GENERAL ELECTION | August 6, 2026
      </div>

      <div className="race-matchup-grid">
        {ordered[0].candidate
          ? <CandidateMatchupCard candidate={ordered[0].candidate} generalElection={generalElection} />
          : <EmptyNomineeCard party={ordered[0].party} />
        }
        <div className="race-matchup-vs d-flex align-items-center justify-content-center fw-bold text-muted">
          vs
        </div>
        {ordered[1].candidate
          ? <CandidateMatchupCard candidate={ordered[1].candidate} generalElection={generalElection} />
          : <EmptyNomineeCard party={ordered[1].party} />
        }
      </div>

      <div className="text-center mt-3" style={{ fontSize: '0.72rem', color: '#6c757d', fontWeight: 700 }}>
        {statusLabel}
      </div>
    </div>
  );
};

const OpenMatchup = ({ candidates, generalElection }) => {
  const statusLabel = generalElection?.precinctsReporting != null
    ? `${generalElection.precinctsReporting} of ${generalElection.precinctsTotal} precincts`
    : 'Results pending';

  return (
    <div className="rounded p-3" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
      <div className="text-center fw-bold mb-3" style={{ fontSize: '0.76rem', letterSpacing: '0.08em', color: '#495057' }}>
        GENERAL ELECTION | August 6, 2026
      </div>

      <div className="race-candidate-grid">
        {candidates.map((candidate) => (
          <CandidateMatchupCard
            key={candidate.id}
            candidate={candidate}
            generalElection={generalElection}
          />
        ))}
      </div>

      <div className="text-center mt-3" style={{ fontSize: '0.72rem', color: '#6c757d', fontWeight: 700 }}>
        {statusLabel}
      </div>
    </div>
  );
};

const SinglePartyView = ({ candidate, party, generalElection }) => (
  <div className="rounded p-3" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
    {candidate
      ? <CandidateMatchupCard candidate={candidate} generalElection={generalElection} />
      : <EmptyNomineeCard party={party} />
    }
  </div>
);

const RaceBracket = ({ race, topParty = 'R', filter = 'both' }) => {
  const {
    title,
    voteFor,
    republican: rCandidates,
    democrat: dCandidates,
    other: otherCandidates = [],
    generalElection,
    explainer,
  } = race;
  const rCandidate = rCandidates.find(c => c.primary_result === 'won') || rCandidates[0] || null;
  const dCandidate = dCandidates.find(c => c.primary_result === 'won') || dCandidates[0] || null;
  const orderedPartyCandidates = topParty === 'D'
    ? [...dCandidates, ...rCandidates]
    : [...rCandidates, ...dCandidates];
  const openCandidates = [...orderedPartyCandidates.filter(Boolean), ...otherCandidates];
  const hasMultiplePartyNominees = rCandidates.length > 1 || dCandidates.length > 1;
  const useOpenField = otherCandidates.length > 0
    || hasMultiplePartyNominees
    || (!rCandidate && !dCandidate);

  if (filter === 'R' && !rCandidate) return null;
  if (filter === 'D' && !dCandidate) return null;

  return (
    <div className="card shadow-sm" style={{ flex: 1, borderTop: `4px solid ${CARD_ACCENT}` }}>
      <div className="card-header fw-bold text-center bg-dark text-white py-2" style={{ fontSize: '0.92rem' }}>
        <div>{title}</div>
        {voteFor > 1 && (
          <div style={{ fontSize: '0.72rem', fontWeight: 600, marginTop: '0.2rem', opacity: 0.9 }}>
            Vote for up to {voteFor} candidates
          </div>
        )}
      </div>
      <div className="card-body p-2 d-flex flex-column gap-2">
        {filter === 'R' && (
          rCandidates.length > 1
            ? <OpenMatchup candidates={rCandidates} generalElection={generalElection} />
            : <SinglePartyView candidate={rCandidate} party="R" generalElection={generalElection} />
        )}
        {filter === 'D' && (
          dCandidates.length > 1
            ? <OpenMatchup candidates={dCandidates} generalElection={generalElection} />
            : <SinglePartyView candidate={dCandidate} party="D" generalElection={generalElection} />
        )}
        {filter === 'both' && (
          useOpenField ? (
            <OpenMatchup candidates={openCandidates} generalElection={generalElection} />
          ) : (
            <Matchup
              rCandidate={rCandidate}
              dCandidate={dCandidate}
              generalElection={generalElection}
              topParty={topParty}
            />
          )
        )}
        {explainer && (
          <div className="rounded p-2" style={{ background: '#fff', border: '1px solid #e9ecef', fontSize: '0.72rem', color: '#495057', lineHeight: 1.35 }}>
            <div><strong>Office:</strong> {explainer.role}</div>
            <div className="mt-1"><strong>Why it matters:</strong> {explainer.voterImpact}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RaceBracket;
